import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from './cn';
import { InfoTip } from './InfoTip';

/**
 * A surface.
 *
 * Deliberately plain, and deliberately not the default container for
 * everything: a page where every paragraph sits in its own bordered box has
 * used the strongest grouping signal it has on content that was never
 * grouped, and then has nothing left to distinguish the parts that are.
 */
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-card border border-neutral-200 bg-white shadow-card', className)}>
      {children}
    </div>
  );
}

export function CardHeader({ title, hint, actions, className }: {
  title: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3 px-5 py-4', className)}>
      <div className="min-w-0">
        {/* The explanation behind ⓘ, not under the title (owner's review, 14 Oct 2026). */}
        <h3 className="flex items-center gap-1 text-h4 text-neutral-900">
          <span className="min-w-0">{title}</span>
          {hint && <InfoTip label={`About ${typeof title === 'string' ? title : 'this section'}`}>{hint}</InfoTip>}
        </h3>
      </div>
      {/* Wraps rather than overflowing a phone when there are three buttons. */}
      {actions && <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('px-5 py-4', className)}>{children}</div>;
}

/**
 * A row of `StatCard`s: the grid its `className` asks for from `md`, and one
 * line that scrolls sideways on a phone.
 *
 * Four tiles stacked one under another were 560px of numbers before the
 * first invoice. On a phone they are a strip the height of one tile, with
 * the next tile cut by the edge so it is plain there is more to the right.
 */
export function StatStrip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        // The caller's grid, untouched from `md`; only below it is this a strip.
        'grid gap-3 max-md:flex max-md:snap-x max-md:overflow-x-auto max-md:pb-1',
        // Narrow enough that a third tile shows at the edge: it says there is more.
        'max-md:[&>*]:w-[9.25rem] max-md:[&>*]:shrink-0 max-md:[&>*]:snap-start',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A single number, with what it is and how it is moving.
 *
 * Four to six of these at the top of a dashboard. Twelve is a wall of digits
 * that answers no question, because the reader has to work out which of the
 * twelve they came for.
 */
export function StatCard({
  label, value, sub, trend, icon, tone = 'neutral', valueTone, loading, onClick, className,
}: {
  label: string;
  value?: ReactNode;
  sub?: ReactNode;
  /** Signed percentage against the previous period. Rendered with its direction. */
  trend?: {
    value: number;
    label?: string;
    /**
     * `neutral` for a count whose rise is neither good nor bad (cases raised,
     * workers added): grey instead of green/red, so colour never passes a
     * verdict the number does not carry.
     */
    tone?: 'goodWhenUp' | 'neutral';
  };
  icon?: ReactNode;
  /** `attention` draws the tile as something to act on rather than a fact. */
  tone?: 'neutral' | 'attention';
  /**
   * Colours the number itself. For counts whose meaning IS their state —
   * "overdue: 7" is not a neutral fact — while leaving the rest of the tile
   * quiet, so a row of five does not turn into a row of five colours.
   */
  valueTone?: 'covered' | 'dueSoon' | 'overdue' | 'pending';
  loading?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  const Wrapper = onClick ? 'button' : 'div';
  // The icon sits in a tinted square (the 29 Oct 2026 layout). Navy for a
  // plain fact; the state's own pale fill when the number IS a state, so the
  // chip and the number agree and a row of five is still mostly quiet.
  const chip = tone === 'attention' ? 'bg-danger-100 text-danger-600'
    : valueTone ? CHIP[valueTone]
    : 'bg-primary-50 text-primary-600';
  return (
    <Wrapper
      {...(onClick ? { onClick, type: 'button' as const } : {})}
      className={cn(
        'flex flex-col rounded-card border bg-white text-left shadow-card',
        tone === 'attention' ? 'border-danger-600/30' : 'border-neutral-200',
        onClick && 'group transition-colors duration-hover hover:border-primary-300',
        className,
      )}
    >
      <div className="flex-1 p-4 max-md:p-3">
        <div className="flex items-center gap-2.5">
          {icon && (
            <span aria-hidden className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] [&>svg]:h-5 [&>svg]:w-5 max-md:h-8 max-md:w-8', chip)}>
              {icon}
            </span>
          )}
          <span className={cn('min-w-0 text-small font-medium max-md:text-caption',
            tone === 'attention' ? 'text-danger-600' : 'text-neutral-700')}>
            {label}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1 max-md:mt-2">
          {/* 24px, not 32: a money figure like ₹2,832.00 at 32px bold read as
              shouting next to everything else (owner, 29 Oct 2026). */}
          <span className={cn('text-h2 font-semibold tabular-nums max-md:text-h3',
            valueTone === 'overdue' ? 'text-overdue-600'
            : valueTone === 'dueSoon' ? 'text-dueSoon-600'
            : valueTone === 'covered' ? 'text-covered-600'
            : valueTone === 'pending' ? 'text-pending-600'
            : 'text-neutral-900')}>
            {loading
              ? <span className="inline-block h-8 w-16 animate-pulse rounded bg-neutral-100" />
              : value ?? 0}
          </span>
          {trend && (
            <span className={cn('inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-caption font-medium',
              trend.tone === 'neutral' ? 'bg-neutral-100 text-neutral-700'
              : trend.value >= 0 ? 'bg-success-100 text-success-600' : 'bg-danger-100 text-danger-600')}>
              <span aria-hidden>{trend.value >= 0 ? '↑' : '↓'}</span>
              {Math.abs(trend.value).toFixed(1)}%
              {/* Direction is stated for anyone who cannot see the arrow. */}
              <span className="sr-only">{trend.value >= 0 ? 'up' : 'down'}</span>
              {trend.label && <span className="ml-1 font-normal text-neutral-500">{trend.label}</span>}
            </span>
          )}
        </div>
      </div>

      {/* The line under a rule: what the number means, and — when the tile
          opens something — the arrow that says so. */}
      {(sub || onClick) && (
        <div className="flex items-center gap-2 border-t border-neutral-100 px-4 py-2.5 text-caption text-neutral-500 max-md:px-3 max-md:py-2">
          <span className="min-w-0 flex-1">{sub}</span>
          {onClick && (
            <ArrowRight aria-hidden className="h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-hover group-hover:translate-x-0.5 group-hover:text-primary-600" />
          )}
        </div>
      )}
    </Wrapper>
  );
}

const CHIP: Record<'covered' | 'dueSoon' | 'overdue' | 'pending', string> = {
  covered: 'bg-covered-100 text-covered-600',
  dueSoon: 'bg-dueSoon-100 text-dueSoon-600',
  overdue: 'bg-overdue-100 text-overdue-600',
  pending: 'bg-pending-100 text-pending-600',
};
